import { useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import {
  Clock,
  Upload,
  Plus,
  Trash2,
  Save,
  Zap,
  FileSpreadsheet,
  FileText,
  Wand2,
  AlertCircle,
  Palette,
  Mail
} from 'lucide-react';
import {
  klassenbuchApi,
  type ScheduleConfig,
  type ScheduleConfigDocument,
  type ScheduleGrid,
  type SchedulePeriod,
  type SchoolDay,
  type ImportSource,
  type SubjectColorMap,
  type KlassenbuchSubjectRow,
  getScheduleGridById
} from '../../lib/klassenbuchApi';
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
  TableScrollArea
} from '../ui';

const DAY_LABELS: Array<{ key: SchoolDay; short: string; long: string }> = [
  { key: 'mon', short: 'Mo', long: 'Montag' },
  { key: 'tue', short: 'Di', long: 'Dienstag' },
  { key: 'wed', short: 'Mi', long: 'Mittwoch' },
  { key: 'thu', short: 'Do', long: 'Donnerstag' },
  { key: 'fri', short: 'Fr', long: 'Freitag' },
  { key: 'sat', short: 'Sa', long: 'Samstag' },
  { key: 'sun', short: 'So', long: 'Sonntag' }
];

const DEFAULT_CONFIG: ScheduleConfig = {
  schoolDays: ['mon', 'tue', 'wed', 'thu', 'fri'],
  dayStart: '08:00',
  lessonsPerDay: 8,
  defaultLessonMinutes: 45,
  defaultBreakMinutes: 5,
  periods: []
};

const DEFAULT_CONFIG_DOC: ScheduleConfigDocument = {
  version: 2,
  defaultGridId: 'grid-default',
  grids: [
    {
      id: 'grid-default',
      name: 'Standard',
      ...DEFAULT_CONFIG
    }
  ]
};

/** Nur für Farbwähler, wenn noch keine Farbe gesetzt ist */
const COLOR_PICKER_FALLBACK = '#94a3b8';

const PRESET_SUBJECT_COLORS = [
  '#2563eb',
  '#059669',
  '#d97706',
  '#dc2626',
  '#7c3aed',
  '#db2777',
  '#0891b2',
  '#4d7c0f'
] as const;

function normalizeHexInput(raw: string): string | null {
  const t = String(raw || '').trim();
  if (!t) return null;
  if (/^#[0-9A-Fa-f]{6}$/.test(t)) return t.toLowerCase();
  if (/^[0-9A-Fa-f]{6}$/.test(t)) return `#${t.toLowerCase()}`;
  return null;
}

function addMinutes(hhmm: string, min: number): string {
  const [h, m] = hhmm.split(':').map((x) => Number(x));
  const total = h * 60 + m + min;
  const hh = Math.floor((total % (24 * 60)) / 60)
    .toString()
    .padStart(2, '0');
  const mm = (total % 60).toString().padStart(2, '0');
  return `${hh}:${mm}`;
}

function generatePeriods(cfg: ScheduleConfig, startFromZero: boolean): SchedulePeriod[] {
  const periods: SchedulePeriod[] = [];
  let cursor = cfg.dayStart || '08:00';
  const count = Math.max(0, Math.min(20, Number(cfg.lessonsPerDay) || 0));
  const lessonMin = Math.max(1, Number(cfg.defaultLessonMinutes) || 45);
  const breakMin = Math.max(0, Number(cfg.defaultBreakMinutes) || 0);
  for (let i = 0; i < count; i += 1) {
    const start = cursor;
    const end = addMinutes(start, lessonMin);
    const period_no = startFromZero ? i : i + 1;
    periods.push({
      period_no,
      label: `${period_no}. Stunde`,
      start_time: start,
      end_time: end,
      is_break: false
    });
    cursor = breakMin > 0 ? addMinutes(end, breakMin) : end;
  }
  return periods;
}

function maxConfiguredPeriodNo(periods: SchedulePeriod[]): number | null {
  let m: number | null = null;
  for (const p of periods) {
    const n = Number(p.period_no);
    if (Number.isFinite(n)) m = m === null ? n : Math.max(m, n);
  }
  return m;
}

function nextPeriodNo(periods: SchedulePeriod[]): number {
  const max = maxConfiguredPeriodNo(periods);
  return max === null ? 1 : max + 1;
}

function createGridId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID().slice(0, 36);
  }
  return `grid-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`.slice(0, 36);
}

type ImportCardProps = {
  source: ImportSource;
  title: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  withFile: boolean;
  fileAccept?: string;
};

function ImportCard({ source, title, description, icon: Icon, withFile, fileAccept }: ImportCardProps) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [running, setRunning] = useState(false);

  const onRun = async () => {
    if (withFile && !selectedFile) {
      toast.error('Bitte zuerst eine Datei auswaehlen.');
      return;
    }
    setRunning(true);
    try {
      await klassenbuchApi.importSchedule(source, withFile ? selectedFile : null);
      toast.success('Import gestartet');
    } catch (err) {
      const msg =
        (err as { response?: { data?: { hint?: string; error?: string } } })?.response?.data?.hint ||
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        (err as Error)?.message ||
        'Import fehlgeschlagen';
      toast(msg, { icon: 'info' });
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="card flex flex-col gap-3 p-4">
      <div className="flex items-start gap-2">
        <div className="rounded-md bg-blue-50 p-2 text-blue-600 dark:bg-primary-950/40 dark:text-primary-400">
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <div className="font-medium text-gray-900 dark:text-gray-100">{title}</div>
          <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{description}</div>
        </div>
      </div>
      {withFile && (
        <div className="flex items-center gap-2">
          <input
            ref={fileRef}
            type="file"
            accept={fileAccept}
            className="hidden"
            onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
          />
          <Button type="button" size="sm" variant="outline" onClick={() => fileRef.current?.click()}>
            Datei waehlen
          </Button>
          <span className="truncate text-xs text-gray-500 dark:text-gray-400">
            {selectedFile ? selectedFile.name : 'Keine Datei ausgewaehlt'}
          </span>
        </div>
      )}
      <div className="mt-auto">
        <Button
          type="button"
          size="sm"
          variant="primary"
          disabled={running}
          onClick={onRun}
          className="gap-1.5"
        >
          <Upload className="h-4 w-4" />
          {running ? 'Laeuft…' : 'Importieren'}
        </Button>
      </div>
    </div>
  );
}

export default function KlassenbuchSettings() {
  const [configDoc, setConfigDoc] = useState<ScheduleConfigDocument>(DEFAULT_CONFIG_DOC);
  const [activeGridId, setActiveGridId] = useState<string>(DEFAULT_CONFIG_DOC.defaultGridId);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [subjectPack, setSubjectPack] = useState<{
    subjects: KlassenbuchSubjectRow[];
    draft: Record<string, string | null>;
  } | null>(null);
  const [savingSubjectColors, setSavingSubjectColors] = useState(false);
  const [emailNotificationsEnabled, setEmailNotificationsEnabled] = useState(true);
  const [savingEmailNotifications, setSavingEmailNotifications] = useState(false);
  /** Wenn true: automatische Perioden als 0 … n−1 (0. Stunde), sonst 1 … n */
  const [periodAutoFromZero, setPeriodAutoFromZero] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      klassenbuchApi.getConfig(),
      klassenbuchApi.getSubjectColors().catch(() => ({
        colors: {} as SubjectColorMap,
        subjects: [] as KlassenbuchSubjectRow[]
      })),
      klassenbuchApi.getEmailNotificationsConfig().catch(() => ({ enabled: true }))
    ])
      .then(([data, sc, emailCfg]) => {
        if (cancelled) return;
        setConfigDoc(data);
        setActiveGridId(data.defaultGridId);
        const draft: Record<string, string | null> = {};
        for (const s of sc.subjects) {
          draft[String(s.id)] = sc.colors[String(s.id)] || null;
        }
        setSubjectPack({ subjects: sc.subjects, draft });
        setEmailNotificationsEnabled(!!emailCfg.enabled);
      })
      .catch((err) => {
        console.error('[KlassenbuchSettings] load', err);
        if (!cancelled) toast.error('Konnte Klassenbuch-Konfiguration nicht laden.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const activeGrid = useMemo(
    () => getScheduleGridById(configDoc, activeGridId),
    [configDoc, activeGridId]
  );

  const updateActiveGrid = (updater: (grid: ScheduleGrid) => ScheduleGrid) => {
    setConfigDoc((prev) => ({
      ...prev,
      grids: prev.grids.map((grid) => (grid.id === activeGrid.id ? updater(grid) : grid))
    }));
  };

  const addGrid = () => {
    const id = createGridId();
    setConfigDoc((prev) => {
      const next: ScheduleGrid = {
        id,
        name: `Raster ${prev.grids.length + 1}`,
        ...DEFAULT_CONFIG
      };
      return { ...prev, grids: [...prev.grids, next] };
    });
    setActiveGridId(id);
  };

  const removeActiveGrid = () => {
    if (configDoc.grids.length <= 1) {
      toast.error('Mindestens ein Zeitraster muss bestehen bleiben.');
      return;
    }
    setConfigDoc((prev) => {
      const remaining = prev.grids.filter((grid) => grid.id !== activeGrid.id);
      const nextDefault =
        prev.defaultGridId === activeGrid.id ? remaining[0]?.id || prev.defaultGridId : prev.defaultGridId;
      return {
        ...prev,
        defaultGridId: nextDefault,
        grids: remaining
      };
    });
    setActiveGridId((prev) => {
      if (prev !== activeGrid.id) return prev;
      const fallback = configDoc.grids.find((grid) => grid.id !== activeGrid.id);
      return fallback?.id || prev;
    });
  };

  const setDefaultGrid = (gridId: string) => {
    setConfigDoc((prev) => ({ ...prev, defaultGridId: gridId }));
  };

  const updateGridName = (name: string) => {
    updateActiveGrid((grid) => ({ ...grid, name }));
  };

  const toggleDay = (day: SchoolDay) => {
    updateActiveGrid((grid) => {
      const has = grid.schoolDays.includes(day);
      const next = has ? grid.schoolDays.filter((d) => d !== day) : [...grid.schoolDays, day];
      const order: SchoolDay[] = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
      return { ...grid, schoolDays: order.filter((d) => next.includes(d)) };
    });
  };

  const autoGenerate = () => {
    const periods = generatePeriods(activeGrid, periodAutoFromZero);
    updateActiveGrid((grid) => ({ ...grid, periods }));
    toast.success(`${periods.length} Perioden berechnet`);
  };

  const updatePeriod = (idx: number, patch: Partial<SchedulePeriod>) => {
    updateActiveGrid((grid) => ({
      ...grid,
      periods: grid.periods.map((p, i) => (i === idx ? { ...p, ...patch } : p))
    }));
  };

  const removePeriod = (idx: number) => {
    updateActiveGrid((grid) => ({
      ...grid,
      periods: grid.periods.filter((_, i) => i !== idx)
    }));
  };

  const addPeriod = () => {
    updateActiveGrid((grid) => {
      const last = grid.periods[grid.periods.length - 1];
      const start = last ? last.end_time : grid.dayStart;
      const end = addMinutes(start, grid.defaultLessonMinutes);
      const nextNo = nextPeriodNo(grid.periods);
      const next: SchedulePeriod = {
        period_no: nextNo,
        label: `${nextNo}. Stunde`,
        start_time: start,
        end_time: end,
        is_break: false
      };
      return { ...grid, periods: [...grid.periods, next] };
    });
  };

  const saveSubjectColors = async () => {
    if (!subjectPack) return;
    setSavingSubjectColors(true);
    try {
      const colors: SubjectColorMap = {};
      for (const s of subjectPack.subjects) {
        const hex = normalizeHexInput(subjectPack.draft[String(s.id)] || '');
        if (hex) colors[String(s.id)] = hex;
      }
      const saved = await klassenbuchApi.saveSubjectColors(colors);
      const draft: Record<string, string | null> = {};
      for (const s of subjectPack.subjects) {
        draft[String(s.id)] = saved[String(s.id)] || null;
      }
      setSubjectPack({ subjects: subjectPack.subjects, draft });
      toast.success('Fachfarben gespeichert');
    } catch (err) {
      const msg =
        (err as { response?: { data?: { error?: string; details?: string[] } } })?.response?.data
          ?.error ||
        (err as Error)?.message ||
        'Speichern fehlgeschlagen';
      const details = (err as { response?: { data?: { details?: string[] } } })?.response?.data
        ?.details;
      toast.error(details?.length ? `${msg}: ${details.join(', ')}` : msg);
    } finally {
      setSavingSubjectColors(false);
    }
  };

  const saveEmailNotifications = async () => {
    setSavingEmailNotifications(true);
    try {
      const saved = await klassenbuchApi.saveEmailNotificationsConfig(emailNotificationsEnabled);
      setEmailNotificationsEnabled(!!saved.enabled);
      toast.success('E-Mail-Benachrichtigungen gespeichert');
    } catch (err) {
      const msg =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        (err as Error)?.message ||
        'Speichern fehlgeschlagen';
      toast.error(msg);
    } finally {
      setSavingEmailNotifications(false);
    }
  };

  const suggestSubjectColors = () => {
    setSubjectPack((prev) => {
      if (!prev) return prev;
      const draft = { ...prev.draft };
      prev.subjects.forEach((s, i) => {
        draft[String(s.id)] = PRESET_SUBJECT_COLORS[i % PRESET_SUBJECT_COLORS.length];
      });
      return { ...prev, draft };
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const saved = await klassenbuchApi.saveConfig(configDoc);
      setConfigDoc(saved);
      if (!saved.grids.some((grid) => grid.id === activeGridId)) {
        setActiveGridId(saved.defaultGridId);
      }
      toast.success('Konfiguration gespeichert');
    } catch (err) {
      const msg =
        (err as { response?: { data?: { error?: string; details?: string[] } } })?.response?.data
          ?.error ||
        (err as Error)?.message ||
        'Speichern fehlgeschlagen';
      const details = (err as { response?: { data?: { details?: string[] } } })?.response?.data
        ?.details;
      toast.error(details?.length ? `${msg}: ${details.join(', ')}` : msg);
    } finally {
      setSaving(false);
    }
  };

  const totalDuration = useMemo(() => {
    if (!activeGrid.periods.length) return null;
    const first = activeGrid.periods[0].start_time;
    const last = activeGrid.periods[activeGrid.periods.length - 1].end_time;
    return `${first} – ${last}`;
  }, [activeGrid.periods]);

  if (loading) {
    return <div className="text-sm text-gray-500 dark:text-gray-400">Lade Konfiguration…</div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-50">
          Klassenbuch-Konfiguration
        </h2>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Rahmenzeiten, Stundenraster und Import-Quellen fuer das digitale Klassenbuch.
        </p>
      </div>

      <section className="card p-4 space-y-3">
        <div className="flex items-center justify-between gap-2">
          <h3 className="font-medium text-gray-900 dark:text-gray-100">Zeitraster</h3>
          <Button type="button" size="sm" variant="outline" onClick={addGrid} className="gap-1">
            <Plus className="h-4 w-4" />
            Raster anlegen
          </Button>
        </div>
        <div className="flex flex-wrap gap-2">
          {configDoc.grids.map((grid) => (
            <Button
              key={grid.id}
              type="button"
              size="sm"
              variant={activeGrid.id === grid.id ? 'primary' : 'outline'}
              onClick={() => setActiveGridId(grid.id)}
            >
              {grid.name}
              {configDoc.defaultGridId === grid.id ? ' (Standard)' : ''}
            </Button>
          ))}
        </div>
        <div className="grid gap-3 md:grid-cols-3">
          <label className="text-sm md:col-span-2">
            <span className="mb-1 block text-gray-700 dark:text-gray-300">Name des aktiven Rasters</span>
            <input
              value={activeGrid.name}
              onChange={(e) => updateGridName(e.target.value)}
              className="input-field w-full"
              placeholder="z. B. Standort Nord"
            />
          </label>
          <div className="flex items-end gap-2">
            <Button
              type="button"
              size="sm"
              variant={configDoc.defaultGridId === activeGrid.id ? 'primary' : 'outline'}
              onClick={() => setDefaultGrid(activeGrid.id)}
            >
              Als Standard
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={removeActiveGrid}
              disabled={configDoc.grids.length <= 1}
              className="gap-1 text-red-600 dark:text-red-400"
            >
              <Trash2 className="h-4 w-4" />
              Entfernen
            </Button>
          </div>
        </div>
      </section>

      {/* Rahmenzeiten */}
      <section className="card p-4">
        <div className="mb-3 flex items-center gap-2">
          <Clock className="h-4 w-4 text-blue-600 dark:text-primary-400" />
          <h3 className="font-medium text-gray-900 dark:text-gray-100">Rahmenzeiten</h3>
        </div>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
          <label className="text-sm">
            <span className="mb-1 block text-gray-700 dark:text-gray-300">Tagesstart</span>
            <input
              type="time"
              value={activeGrid.dayStart}
              onChange={(e) => updateActiveGrid((grid) => ({ ...grid, dayStart: e.target.value }))}
              className="input w-full dark:[color-scheme:dark]"
            />
          </label>
          <label className="text-sm">
            <span className="mb-1 block text-gray-700 dark:text-gray-300">Stunden pro Tag</span>
            <input
              type="number"
              min={1}
              max={20}
              value={activeGrid.lessonsPerDay}
              onChange={(e) => updateActiveGrid((grid) => ({ ...grid, lessonsPerDay: Number(e.target.value) || 0 }))}
              className="input w-full"
            />
          </label>
          <label className="text-sm">
            <span className="mb-1 block text-gray-700 dark:text-gray-300">Stundenlaenge (min)</span>
            <input
              type="number"
              min={1}
              max={240}
              value={activeGrid.defaultLessonMinutes}
              onChange={(e) =>
                updateActiveGrid((grid) => ({ ...grid, defaultLessonMinutes: Number(e.target.value) || 0 }))
              }
              className="input w-full"
            />
          </label>
          <label className="text-sm">
            <span className="mb-1 block text-gray-700 dark:text-gray-300">Pausenlaenge (min)</span>
            <input
              type="number"
              min={0}
              max={120}
              value={activeGrid.defaultBreakMinutes}
              onChange={(e) =>
                updateActiveGrid((grid) => ({ ...grid, defaultBreakMinutes: Number(e.target.value) || 0 }))
              }
              className="input w-full"
            />
          </label>
        </div>

        <div className="mt-3">
          <span className="mb-1 block text-sm text-gray-700 dark:text-gray-300">Schultage</span>
          <div className="flex flex-wrap gap-2">
            {DAY_LABELS.map((d) => {
              const active = activeGrid.schoolDays.includes(d.key);
              return (
                <Button
                  key={d.key}
                  type="button"
                  size="sm"
                  variant={active ? 'primary' : 'outline'}
                  onClick={() => toggleDay(d.key)}
                  title={d.long}
                >
                  {d.short}
                </Button>
              );
            })}
          </div>
        </div>

        <div className="mt-4 flex flex-col gap-2">
          <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
            <input
              type="checkbox"
              checked={periodAutoFromZero}
              onChange={(e) => setPeriodAutoFromZero(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-900"
            />
            <span>Bei automatischer Berechnung mit 0. Stunde beginnen (Nummern 0 … n−1)</span>
          </label>
          <div className="flex flex-wrap items-center gap-2">
            <Button type="button" size="sm" variant="outline" onClick={autoGenerate} className="gap-1.5">
              <Zap className="h-4 w-4" />
              Perioden automatisch berechnen
            </Button>
            <Button
              type="button"
              size="sm"
              variant="primary"
              onClick={() => void save()}
              disabled={saving}
              className="gap-1.5"
            >
              <Save className="h-4 w-4" />
              {saving ? 'Speichere…' : 'Speichern'}
            </Button>
          </div>
        </div>
      </section>

      <section className="card p-4 dark:bg-gray-950/50">
        <div className="mb-3 flex items-center gap-2">
          <Mail className="h-4 w-4 text-blue-600 dark:text-primary-400" />
          <h3 className="font-medium text-gray-900 dark:text-gray-100">E-Mail-Benachrichtigungen</h3>
        </div>
        <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
          <input
            type="checkbox"
            checked={emailNotificationsEnabled}
            onChange={(e) => setEmailNotificationsEnabled(e.target.checked)}
            className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-900"
          />
          <span>Automatische Klassenbuch-E-Mails aktivieren (Hausaufgaben, Mitteilungen, Notizzettel)</span>
        </label>
        <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
          Wenn deaktiviert, werden Klassenbuch-E-Mails nicht versendet, auch wenn einzelne Aktionen
          "Benachrichtigen" auslösen.
        </p>
        <div className="mt-3">
          <Button
            type="button"
            size="sm"
            variant="primary"
            onClick={() => void saveEmailNotifications()}
            disabled={savingEmailNotifications}
          >
            {savingEmailNotifications ? 'Speichere…' : 'E-Mail-Einstellung speichern'}
          </Button>
        </div>
      </section>

      {/* Fachfarben */}
      <section className="card p-4 dark:bg-gray-950/50">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between mb-3">
          <div className="flex items-center gap-2">
            <Palette className="h-4 w-4 text-blue-600 dark:text-primary-400" />
            <h3 className="font-medium text-gray-900 dark:text-gray-100">Fachfarben</h3>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button type="button" size="sm" variant="outline" onClick={suggestSubjectColors}>
              Vorschlaege setzen
            </Button>
            <Button
              type="button"
              size="sm"
              variant="primary"
              disabled={savingSubjectColors || !subjectPack}
              onClick={() => void saveSubjectColors()}
            >
              {savingSubjectColors ? 'Speichere…' : 'Fachfarben speichern'}
            </Button>
          </div>
        </div>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
          Farben gelten fuer das Klassenbuch und die Stundenplan-Ansicht (sobald eine Fach-Zuordnung
          vorliegt).
        </p>
        {!subjectPack || subjectPack.subjects.length === 0 ? (
          <p className="text-sm italic text-gray-500 dark:text-gray-400">
            Keine aktiven Faecher in der Stammdaten-Tabelle{' '}
            <code className="rounded bg-gray-100 px-1 text-xs dark:bg-gray-800 dark:text-gray-200">
              subjects
            </code>{' '}
            gefunden.
          </p>
        ) : (
          <TableScrollArea>
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeaderCell>Fach</TableHeaderCell>
                  <TableHeaderCell className="hidden sm:table-cell">Kuerzel</TableHeaderCell>
                  <TableHeaderCell>Farbe</TableHeaderCell>
                  <TableHeaderCell className="w-32">Aktion</TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {subjectPack.subjects.map((s) => {
                  const idKey = String(s.id);
                  const hex = subjectPack.draft[idKey];
                  const normalized = hex ? normalizeHexInput(hex) : null;
                  const pickerValue = normalized || COLOR_PICKER_FALLBACK;
                  return (
                    <TableRow key={s.id}>
                      <TableCell className="font-medium text-gray-900 dark:text-gray-100">
                        {s.subject_name}
                      </TableCell>
                      <TableCell className="hidden sm:table-cell text-gray-500 dark:text-gray-400 text-sm">
                        {s.subject_code?.trim() || '—'}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap items-center gap-2">
                          <input
                            type="color"
                            aria-label={`Farbe ${s.subject_name}`}
                            className="h-9 w-12 cursor-pointer rounded border border-gray-300 bg-white p-0 dark:border-gray-600 dark:bg-gray-900"
                            value={pickerValue}
                            onChange={(e) => {
                              const v = e.target.value;
                              setSubjectPack((prev) =>
                                prev
                                  ? {
                                      ...prev,
                                      draft: { ...prev.draft, [idKey]: v.toLowerCase() }
                                    }
                                  : prev
                              );
                            }}
                          />
                          <input
                            type="text"
                            inputMode="text"
                            placeholder="#2563eb"
                            className="input-field w-28 text-sm"
                            value={hex || ''}
                            onChange={(e) => {
                              const v = e.target.value;
                              setSubjectPack((prev) =>
                                prev
                                  ? {
                                      ...prev,
                                      draft: { ...prev.draft, [idKey]: v.trim() === '' ? null : v }
                                    }
                                  : prev
                              );
                            }}
                            onBlur={() => {
                              setSubjectPack((prev) => {
                                if (!prev) return prev;
                                const raw = prev.draft[idKey];
                                const n = normalizeHexInput(String(raw || ''));
                                return {
                                  ...prev,
                                  draft: { ...prev.draft, [idKey]: n }
                                };
                              });
                            }}
                          />
                        </div>
                      </TableCell>
                      <TableCell>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() =>
                            setSubjectPack((prev) =>
                              prev
                                ? { ...prev, draft: { ...prev.draft, [idKey]: null } }
                                : prev
                            )
                          }
                        >
                          Standard
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableScrollArea>
        )}
      </section>

      {/* Perioden-Raster */}
      <section className="card p-4">
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4 text-blue-600 dark:text-primary-400" />
            <h3 className="font-medium text-gray-900 dark:text-gray-100">Stundenraster</h3>
            {totalDuration && (
              <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">Tag: {totalDuration}</span>
            )}
          </div>
          <Button type="button" size="sm" variant="outline" onClick={addPeriod} className="gap-1">
            <Plus className="h-4 w-4" />
            Periode
          </Button>
        </div>

        {activeGrid.periods.length === 0 ? (
          <div className="text-sm italic text-gray-500 dark:text-gray-400">
            Keine Perioden konfiguriert. Nutzen Sie „Perioden automatisch berechnen" oder fuegen Sie
            manuell hinzu.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-left text-xs uppercase tracking-wide text-gray-500 dark:border-gray-700 dark:text-gray-400">
                  <th className="py-2 pr-3">#</th>
                  <th className="py-2 pr-3">Bezeichnung</th>
                  <th className="py-2 pr-3">Start</th>
                  <th className="py-2 pr-3">Ende</th>
                  <th className="py-2 pr-3">Pause</th>
                  <th className="py-2 pr-3" />
                </tr>
              </thead>
              <tbody>
                {activeGrid.periods.map((p, idx) => (
                  <tr
                    key={idx}
                    className="border-b border-gray-100 dark:border-gray-800"
                  >
                    <td className="py-1.5 pr-3">
                      <input
                        type="number"
                        min={0}
                        step={1}
                        value={p.period_no}
                        onChange={(e) => {
                          const v = Number(e.target.value);
                          if (Number.isFinite(v) && v >= 0) {
                            updatePeriod(idx, { period_no: Math.floor(v) });
                          }
                        }}
                        className="input w-16 min-w-0"
                        aria-label="Periodennummer"
                      />
                    </td>
                    <td className="py-1.5 pr-3">
                      <input
                        type="text"
                        value={p.label || ''}
                        onChange={(e) => updatePeriod(idx, { label: e.target.value })}
                        className="input w-full"
                        placeholder={`${p.period_no}. Stunde`}
                      />
                    </td>
                    <td className="py-1.5 pr-3">
                      <input
                        type="time"
                        value={p.start_time}
                        onChange={(e) => updatePeriod(idx, { start_time: e.target.value })}
                        className="input min-w-[6.5rem] dark:[color-scheme:dark]"
                      />
                    </td>
                    <td className="py-1.5 pr-3">
                      <input
                        type="time"
                        value={p.end_time}
                        onChange={(e) => updatePeriod(idx, { end_time: e.target.value })}
                        className="input min-w-[6.5rem] dark:[color-scheme:dark]"
                      />
                    </td>
                    <td className="py-1.5 pr-3">
                      <input
                        type="checkbox"
                        checked={p.is_break}
                        onChange={(e) => updatePeriod(idx, { is_break: e.target.checked })}
                        className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-900"
                      />
                    </td>
                    <td className="py-1.5 pr-3 text-right">
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => removePeriod(idx)}
                        className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                        title="Periode loeschen"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Stundenplan-Import */}
      <section className="card p-4">
        <div className="mb-1 flex items-center gap-2">
          <Upload className="h-4 w-4 text-blue-600 dark:text-primary-400" />
          <h3 className="font-medium text-gray-900 dark:text-gray-100">Stundenplan-Import</h3>
        </div>
        <div className="mb-3 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-800/80 dark:bg-amber-950/40 dark:text-amber-200">
          <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <span>
            Die Import-Quellen sind noch nicht implementiert. Die Buttons rufen die vorbereiteten
            API-Endpoints auf, die aktuell 501 zurueckliefern. Spezifikation siehe{' '}
            <code className="rounded bg-amber-100/90 px-1 dark:bg-amber-900/50 dark:text-amber-100">
              docs/klassenbuch-import-schema.md
            </code>
            .
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <ImportCard
            source="untis"
            title="Untis (GPU/XML)"
            description="Stundenplan-Export aus Untis als XML- oder GPU-Datei"
            icon={FileText}
            withFile
            fileAccept=".xml,.gpu,.txt"
          />
          <ImportCard
            source="davinci"
            title="DaVinci"
            description="Export aus Stueber Systems DaVinci"
            icon={FileText}
            withFile
            fileAccept=".xml,.csv"
          />
          <ImportCard
            source="csv"
            title="CSV-Import"
            description="Eigenes CSV-Format gemaess docs/klassenbuch-import-schema.md"
            icon={FileSpreadsheet}
            withFile
            fileAccept=".csv"
          />
          <ImportCard
            source="generate-from-config"
            title="Aus Rahmenzeiten generieren"
            description="Erzeugt teaching_units anhand der obigen Rahmenzeiten und bestehender Memberships."
            icon={Wand2}
            withFile={false}
          />
        </div>
      </section>
    </div>
  );
}
